import { api } from '../../app/api/baseApi';
import { ALARMS_COMMENTS_CATEGORIES, type CommentCategory } from '../../utils/constants';

export type TimelineCommentRequestBody = {
    entityId?: string;
    serialNumber?: string;
    category: CommentCategory;
    comment: string;
    /** Dual-written for readers that still use the legacy comment field. */
    techSupport: string;
    nameofCustomerContacted: string;
    contactMadeWithCustomerTech: string;
    actionTaken: string;
    noerCallPreference: string;
    alarmCausedbyTech: string;
    eatonRepresentative?: string;
};

type TimelineCommentArgs = {
    body: TimelineCommentRequestBody;
    realmPrefix?: string;
    organizationId?: string;
    /** The host refreshes the affected timeline by ID instead of reloading the list. */
    skipTimelineListRefresh?: boolean;
};

export type GetAllCommentsArgs = {
    /** Required: it is a path segment, so the endpoint is meaningless without it. */
    serialNumber: string;
    page: number;
    size: number;
    realmPrefix?: string;
    /** Overrides the redux-selected org's `organizationId` header (e.g. Service Admin). */
    organizationId?: string;
    /**
     * Tri-state, and `undefined` is **not** the same as `''`:
     *
     * - omitted — defaults to `ALARMS`, preserving the sliding panel's behaviour;
     * - `''` — drops the query parameter, listing comments from every category;
     * - a category — scopes the list to it.
     *
     * Callers that want every category must therefore pass `''` explicitly rather than leaving
     * the field undefined.
     */
    category?: CommentCategory | '';
};

/** Args for the event-scoped `GET /device-comments/upcoming/{id}` read. */
export type GetUpcomingCommentsArgs = {
    /** Required: it is a path segment, so the endpoint is meaningless without it. */
    upcomingId: string;
    realmPrefix?: string;
    /** Overrides the redux-selected org's `organizationId` header, as the sibling reads do. */
    organizationId?: string;
};

/**
 * Envelope returned by the event-scoped comment reads. Only the fields the UI consumes are
 * modelled; the comment shape itself stays in the web package, which owns its rendering.
 */
export type EventCommentsResponse<TComment = unknown> = {
    data?: TComment[];
};

/**
 * Builds the device-comments request path. See `GetAllCommentsArgs.category` for the tri-state
 * semantics of the category argument; `deviceCommentsPath.test.ts` pins the resulting URLs.
 */
export const buildDeviceCommentsPath = ({
    serialNumber,
    page,
    size,
    category = ALARMS_COMMENTS_CATEGORIES,
}: Omit<GetAllCommentsArgs, 'realmPrefix' | 'organizationId'>): string => {
    const searchParams = new URLSearchParams();
    if (category) {
        searchParams.set('category', category);
    }
    searchParams.set('page', String(page));
    searchParams.set('size', String(size));
    return `dm/v1/device-comments/${encodeURIComponent(serialNumber)}?${searchParams.toString()}`;
};

export const timelineApi = api.injectEndpoints({
    endpoints: (builder) => ({
        getCount: builder.mutation<any, { realm?: string; organizationId?: string }>({
            query: ({ realm, organizationId }) => ({
                url: 'alarm/v1/count',
                method: 'POST',
                headers: {
                    ...(realm ? { 'x-realm': realm } : {}),
                    ...(organizationId ? { organizationId: organizationId } : {}),
                },
                body: {
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
        }),
        getTimelineDetailsById: builder.query<any, any>({
            query: (id) => ({
                url: `alarm/v1/timeline/${id}`,
                method: 'GET',
            }),
            providesTags: ['TimelineById'],
        }),
        getTimelineDetailsByIdWithRealm: builder.query<any, any>({
            query: ({ id, realmPrefix }) => ({
                url: `alarm/v1/timeline/${id}`,
                method: 'GET',
                headers: realmPrefix ? { 'x-realm': realmPrefix } : undefined,
            }),
            providesTags: ['TimelineById'],
        }),
        updateAcknowledge: builder.mutation<any, any>({
            query: (body) => ({
                url: `alarm/v1/timeline/acknowledge`,
                method: 'PATCH',
                body,
            }),
            invalidatesTags: (result, error): any => (!error ? ['TimelineById'] : []),
        }),
        getRealmTimeline: builder.query({
            query: ({ body, realmPrefix, organizationId }) => ({
                url: `alarm/v1/timeline/filter`,
                method: 'POST',
                body,
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            keepUnusedDataFor: 0.0001,
            providesTags: ['RealmTimelineTags'],
        }),
        getRealmTimelineById: builder.query({
            query: ({ timelineid, realmPrefix }) => ({
                url: `alarm/v1/timeline/${timelineid}`,
                method: 'GET',
                headers: realmPrefix ? { 'x-realm': realmPrefix } : undefined,
            }),
            providesTags: ['RealmTimelineTags'],
        }),
        ackTimeline: builder.mutation({
            query: ({ realmPrefix, organizationId, body }) => {
                return {
                    url: `/alarm/v1/timeline/acknowledge`,
                    method: 'PATCH',
                    body,
                    headers: {
                        'x-realm': realmPrefix,
                        organizationId: organizationId,
                    },
                };
            },
            // Acknowledging files a comment server-side, so the comments lists must refetch too.
            invalidatesTags: ['RealmTimelineTags', 'TimelineComment'],
        }),

        clearTimeline: builder.mutation({
            query: ({ timelineId, realmPrefix, organizationId, body }) => {
                return {
                    url: `/alarm/v1/timeline/clear/${timelineId}`,
                    method: 'PATCH',
                    body,
                    headers: {
                        'x-realm': realmPrefix,
                        organizationId: organizationId,
                    },
                };
            },
            // Clearing files a comment server-side, so the comments lists must refetch too.
            invalidatesTags: (result: any, error: any): any => (!error ? ['RealmTimelineTags', 'TimelineComment'] : []),
        }),

        getTimelineAllStatus: builder.query<any, any>({
            query: (params: any) => ({
                url: `alarm/v1/timeline/status`,
                method: 'GET',
                ...(params && { params }),
            }),
        }),
        downloadTimeline: builder.mutation({
            query: ({ body, params }) => ({
                url: `alarm/v1/timeline/download`,
                method: 'POST',
                body,
                params,
            }),
        }),

        createSnooze: builder.mutation({
            query: (params) => ({
                url: `alarm/v1/timeline/snooze/device`,
                method: 'POST',
                params,
            }),
            invalidatesTags: (result, error) => (!error ? ['SnoozeByDeviceId', 'DeviceById'] : []),
        }),
        getSnoozeByDeviceId: builder.query({
            query: (params) => ({
                url: `alarm/v1/timeline/snooze`,
                method: 'GET',
                params,
            }),
            providesTags: ['SnoozeByDeviceId'],
        }),
        timelineComment: builder.mutation<unknown, TimelineCommentArgs>({
            query: ({ body, realmPrefix, organizationId }) => ({
                url: `dm/v1/device-comments/device`,
                method: 'POST',
                body,
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            invalidatesTags: (_result, error, { skipTimelineListRefresh }) =>
                error ? [] : skipTimelineListRefresh ? ['TimelineComment'] : ['TimelineComment', 'RealmTimelineTags'],
        }),
        getTimelineComments: builder.query<any, any>({
            query: ({ timelineId, realmPrefix, organizationId }) => ({
                url: `dm/v1/device-comments/timeline/${timelineId}`,
                method: 'GET',
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            providesTags: ['TimelineComment'],
        }),
        /**
         * Event-scoped comments for a single upcoming/prediction record.
         *
         * Deliberately separate from `getTimelineComments`: the timeline endpoint hardcodes
         * `category=ALARMS` on its `entityId` lookup and authorizes the id as an alarm timeline,
         * so an upcoming id sent there returns an empty list (or 403 for non-Eaton-admins).
         */
        getUpcomingComments: builder.query<EventCommentsResponse, GetUpcomingCommentsArgs>({
            query: ({ upcomingId, realmPrefix, organizationId }) => ({
                url: `dm/v1/device-comments/upcoming/${encodeURIComponent(upcomingId)}`,
                method: 'GET',
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            providesTags: ['TimelineComment'],
        }),

        getTimelineTrends: builder.query<any, any>({
            query: ({ params, deviceId }: any) => ({
                url: `dm/v1/devices/${deviceId}/timeline/trends`,
                method: 'GET',
                ...(params && { params }),
            }),
        }),
        getAllComments: builder.query<any, GetAllCommentsArgs>({
            query: ({ serialNumber, page, size, realmPrefix, organizationId, category }) => ({
                url: buildDeviceCommentsPath({ serialNumber, page, size, category }),
                method: 'GET',
                headers: {
                    ...(realmPrefix ? { 'x-realm': realmPrefix } : {}),
                    ...(organizationId ? { organizationId } : {}),
                },
            }),
            providesTags: ['TimelineComment'],
        }),
    }),
});

export const {
    useGetCountMutation,
    useGetTimelineDetailsByIdQuery,
    useGetTimelineDetailsByIdWithRealmQuery,
    useUpdateAcknowledgeMutation,
    useGetRealmTimelineQuery,
    useGetRealmTimelineByIdQuery,
    useLazyGetRealmTimelineByIdQuery,
    useAckTimelineMutation,
    useClearTimelineMutation,
    useGetTimelineAllStatusQuery,
    useDownloadTimelineMutation,
    useCreateSnoozeMutation,
    useGetSnoozeByDeviceIdQuery,
    useTimelineCommentMutation,
    useGetTimelineCommentsQuery,
    useGetUpcomingCommentsQuery,
    useGetTimelineTrendsQuery,
    useGetAllCommentsQuery,
} = timelineApi;
